import { Head } from "@inertiajs/react";
import L from "leaflet";
import { useEffect, useState } from "react";
import "leaflet/dist/leaflet.css";
import { MapContainer, Marker } from "react-leaflet";
import { SalonpageTileLayer } from "./_map-tile-layer";
import {
  encodeSalonpagePathId,
  salonpageExternalUrl,
  salonpageR2ImageUrl,
  salonpageR2LogoUrl,
  salonpageR2MenuImageUrl,
  salonpageStaffVideoUrl,
  salonpageVideoMp4Url,
  salonpageVideoPosterUrl,
  salonpageVideoThumbnailUrl,
} from "@/lib/_url";
import { useVideoPlayer } from "@/lib/_use-video-player";
import { SalonpageFooter } from "./_footer";
import { GoogleMapsFloatingLink } from "./_google_maps_floating_link";
import { NoroshiReserveCta } from "./_noroshi-reserve-cta";
import { salonpageImageCaption } from "./_image-caption";
import { salonpageMetaDescription } from "./_meta-description";
import {
  salonpageImageGroups,
  salonpagePrimaryImage,
} from "./_image-groups";
import type {
  SalonpageShowAverageReservationAmountItem as AverageReservationAmountItem,
  SalonpageShowHeroVideoItem as HeroVideoItem,
  SalonpageShowImageItem as ImageItem,
  SalonpageShowMenuItem as MenuItem,
  SalonpageShowProps as Props,
  SalonpageShowSalon as Salon,
  SalonpageShowStaffItem as StaffItem,
  SalonpageShowStyleItem as StyleItem,
} from "./_props";

export default function SalonShow(props: Props) {
  if (!props.salon) {
    return (
      <main className="grid min-h-screen place-items-center bg-gray-100 px-6 text-zinc-900">
        <p className="text-sm text-zinc-400">Salon not found.</p>
      </main>
    );
  }

  const requiredProps = props as Required<Props>;

  return <SalonShowPage {...requiredProps} />;
}

function SalonShowPage({ salon, heroImagePath, heroVideoId, heroVideoOrientation, heroVideos, images, menus, staff, styles, videos, averageReservationAmounts, featureToggles, meta }: Required<Props>) {
  const [activeKodawariImage, setActiveKodawariImage] = useState<ImageItem | null>(null);
  const { atmosphereImages, kodawariImages } = salonpageImageGroups(images);
  const primaryHeroImage = salonpagePrimaryImage(images);
  const logoImageUrl = salon.logoImageId ? salonpageR2LogoUrl(salon.id, "thumbnail") : "";
  const primaryHero = primaryHeroImage?.id
    ? salonpageR2ImageUrl(salon.id, primaryHeroImage.id, "gallery")
    : logoImageUrl;
  const displayName = salonDisplayName(salon);
  const titleName = salon.nameKana ? `${displayName}【${salon.nameKana}】` : displayName;
  const kodawariItems = splitList(salon.kodawari);
  const kodawariImageCards = kodawariImages.slice(0, 4);
  const menuCards = menus.slice(0, 6);
  const firstVisitAverageReservationAmount = averageReservationAmounts.find((amount) => amount.visitType.includes("初来店"));
  const nearestStation = salon.nearestStation;
  const averageReservationAmountLabel = firstVisitAverageReservationAmount?.amountLabel;
  const hasInfoPills = Boolean(nearestStation || averageReservationAmountLabel);
  const hasValidLocation = numericCoordinate(salon.latitude) != null && numericCoordinate(salon.longitude) != null;
  const hasStoreInformation = Boolean(salon.address || salon.access || salon.businessHours || salon.holiday) || hasValidLocation;

  const description = salonpageMetaDescription(meta.description);

  const computedNoindex = () => {
    if (typeof document === "undefined" || typeof window === "undefined") return false;

    // Mirror server-side noindex logic to preserve semantics after Inertia boots
    // Check rails_stage meta tag (set by server)
    const railsStage = document.querySelector('meta[name="rails_stage"]')?.getAttribute("content");
    // Check URL for draft_key parameter
    const params = new URLSearchParams(window.location.search);
    const hasDraftKey = params.has("draft_key");
    // Check feature toggles from props
    const isNoindexToggled = flagEnabled(featureToggles.noindex);
    const isPagePublished = flagEnabled(featureToggles.pagePublished);

    // Apply same logic as server:
    // - If noindex toggle is enabled
    // - OR stage is not main
    // - OR draft_key present
    // - OR page_published is disabled
    return isNoindexToggled || railsStage !== "main" || hasDraftKey || !isPagePublished;
  };

  return (
    <>
      <Head>
        <title head-key="title">{meta.title}</title>
        <meta head-key="description" name="description" content={description} />
        <link head-key="canonical" rel="canonical" href={salonpageExternalUrl(salon.id)} />
        <meta head-key="og:title" property="og:title" content={meta.title} />
        <meta head-key="og:description" property="og:description" content={description} />
        <meta head-key="og:url" property="og:url" content={salonpageExternalUrl(salon.id)} />
        <meta head-key="og:type" property="og:type" content="website" />
        <meta head-key="og:site_name" property="og:site_name" content="Hairbook Salon" />
        {primaryHero && <meta head-key="og:image" property="og:image" content={primaryHero} />}
        {computedNoindex() && <meta head-key="robots" name="robots" content="noindex, follow" />}
      </Head>

      <div className="min-h-screen bg-gray-100 text-zinc-950 antialiased">
        <main className="mx-auto min-h-screen max-w-[448px] overflow-hidden rounded-b-[22px] bg-white shadow-[0_0_24px_rgba(15,23,42,0.06)]">
          <HeroMedia
            salon={salon}
            displayName={displayName}
            imageUrl={primaryHero}
            videos={normalizedHeroVideos(heroVideos, heroVideoId, heroVideoOrientation)}
            posterUrl={primaryHero || null}
          />

          <section className="space-y-4 border-t border-gray-100 bg-white p-4">
            <div className="flex items-center gap-4">
              {logoImageUrl && (
                <img
                  src={logoImageUrl}
                  alt={`${displayName} logo`}
                  className="h-16 w-16 rounded-md border border-gray-100 bg-white object-contain p-1.5"
                />
              )}
              <div className="min-w-0 flex-1">
                <h1 className="text-lg font-bold leading-6 tracking-tight">{titleName}</h1>
              </div>
            </div>

            {salon.description && <p className="whitespace-pre-line text-[15px] leading-7 text-zinc-800">{salon.description}</p>}

            {hasInfoPills && (
              <div className="space-y-2 text-sm">
                {nearestStation && (
                  <InfoPill
                    label="最寄り駅"
                    value={formatStationName(nearestStation.name)}
                    detail={nearestStation.lineName}
                    title={nearestStation.lineName || formatStationName(nearestStation.name)}
                  />
                )}
                {averageReservationAmountLabel && (
                  <InfoPill
                    label="平均予約金額"
                    value={averageReservationAmountLabel}
                    title={`${firstVisitAverageReservationAmount.visitType}: ${averageReservationAmountLabel}`}
                  />
                )}
              </div>
            )}

            <div
              data-controller="tracking"
              data-action="click->tracking#track"
              data-name="reservation_cta_clicked"
              data-salon-id={salon.id}
              data-location="salon_top"
            >
              <NoroshiReserveCta
                salonId={salon.id}
                enabled={featureToggles.noroshiReserve}
                className="inline-flex h-11 w-full items-center justify-center rounded-md bg-[#070a18] px-4 text-sm font-bold text-white"
              >
                Web予約する
              </NoroshiReserveCta>
            </div>
          </section>

          {staff.length > 0 && <StaffSection salon={salon} staff={staff} />}

          {styles.length > 0 && (
            <Section id="style" title={salon.hpbVertical === "kr" ? "ギャラリー" : "ヘアスタイル"} moreHref={stylesIndexUrl(salon)}>
              <div className="grid grid-cols-3 gap-2">
                {styles.slice(0, 9).map((style) => (
                  <a key={style.id} href={styleDetailUrl(salon, style)} className="min-w-0">
                    <img src={salonpageR2ImageUrl(salon.id, style.id, "thumbnail")} alt={style.name} className="aspect-[3/4] w-full rounded-md bg-gray-100 object-cover" />
                  </a>
                ))}
              </div>
            </Section>
          )}

          {(kodawariImages.length > 0 || kodawariItems.length > 0) && (
            <Section title="こだわり">
              {kodawariImageCards.length > 0 && (
                <div className={`grid gap-3 ${kodawariImageCards.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
                  {kodawariImageCards.map((image) => (
                    <button
                      key={image.id}
                      type="button"
                      onClick={() => setActiveKodawariImage(image)}
                      className="overflow-hidden rounded-md bg-gray-50 text-left transition active:scale-[0.98]"
                      aria-label={`${salonpageImageCaption(image.alt)}の全文を見る`}
                    >
                      <img src={salonpageR2ImageUrl(salon.id, image.id, "thumbnail")} alt={image.alt} className="aspect-[4/3] w-full object-cover" />
                      <div className="p-3">
                        <h3 className="line-clamp-3 text-[13px] font-bold leading-5">{salonpageImageCaption(image.alt)}</h3>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {kodawariItems.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-2">
                  {kodawariItems.map((item) => (
                    <span key={item} className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-zinc-700">
                      {item}
                    </span>
                  ))}
                </div>
              )}
            </Section>
          )}

          {menus.length > 0 && (
            <Section id="menu" title="メニュー">
              <div className="space-y-3">
                {menuCards.map((menu) => (
                  <article key={menu.id} className="overflow-hidden rounded-md border border-gray-200 bg-white">
                    <a href={menuDetailUrl(salon, menu)} className="block">
                      {menu.imageId && (
                        <div className="aspect-[4/3] w-full bg-gray-100">
                          <img
                            src={salonpageR2MenuImageUrl(salon.id, menu.imageId, "gallery")}
                            alt={menu.imageAlt || menu.name}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        </div>
                      )}
                      <div className={menu.imageId ? "relative -mt-4 bg-white p-4" : "relative bg-white p-4"}>
                        <p className="text-[11px] font-bold text-zinc-400">{menu.category || "メニュー"}</p>
                        <div className="mt-1 flex items-start gap-3">
                          <h3 className="min-w-0 flex-1 text-sm font-bold leading-5">{menu.name}</h3>
                          <p className="shrink-0 text-sm font-bold text-zinc-700">{menu.priceLabel}</p>
                        </div>
                        {menu.description && <p className="mt-2 line-clamp-3 whitespace-pre-line text-xs leading-5 text-zinc-500">{menu.description}</p>}
                      </div>
                    </a>
                    <div className="border-t border-gray-100 p-4">
                      <a
                        href={menuReservationRedirectUrl(salon, menu)}
                        data-controller="tracking"
                        data-action="click->tracking#track"
                        data-name="menu_reservation_clicked"
                        data-salon-id={salon.id}
                        data-menu-id={menu.id}
                        data-location="salon_top_menu"
                        className="inline-flex h-11 w-full items-center justify-center rounded-md bg-[#070a18] px-4 text-sm font-bold text-white"
                      >
                        このメニューで予約
                      </a>
                    </div>
                  </article>
                ))}
              </div>

              <a
                href={`/salons/${encodeSalonpagePathId(salon.id)}/menus`}
                className="mt-4 inline-flex h-11 w-full items-center justify-center rounded-md border border-gray-300 px-4 text-sm font-bold"
              >
                すべてのメニューを見る
              </a>
            </Section>
          )}

          {videos && videos.length > 0 && (
            <Section title="動画">
              <div className="grid grid-cols-2 gap-3">
                {videos.map((video) => (
                  <a key={video.videoId} href={`/salons/${encodeSalonpagePathId(salon.id)}/videos/${encodeSalonpagePathId(video.videoId)}`} className="block overflow-hidden rounded-md bg-gray-50">
                    <div className="relative">
                      <img
                        src={salonpageVideoThumbnailUrl(video.videoId)}
                        alt={video.caption || "サロン動画"}
                        className={`w-full object-cover ${video.orientation === "landscape" ? "aspect-video" : "aspect-[3/4]"}`}
                        loading="lazy"
                      />
                      <span className="absolute inset-0 grid place-items-center">
                        <span className="grid size-12 place-items-center rounded-full bg-black/50 text-white">
                          <svg viewBox="0 0 24 24" fill="currentColor" className="ml-0.5 size-5" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
                        </span>
                      </span>
                      {flagEnabled(video.boost) && (
                        <span className="absolute left-2 top-2 rounded-full bg-white/90 px-2 py-1 text-[10px] font-bold text-zinc-800 shadow-sm">
                          おすすめ
                        </span>
                      )}
                    </div>
                    {(video.caption || video.tags.length > 0) && (
                      <div className="space-y-2 p-3">
                        {video.caption && <p className="whitespace-pre-line text-xs leading-5 text-zinc-700">{video.caption}</p>}
                        {video.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1.5" aria-label="動画タグ">
                            {video.tags.map((tag) => (
                              <span key={tag} className="rounded-full bg-white px-2 py-1 text-[10px] font-medium text-zinc-500">
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </a>
                ))}
              </div>
            </Section>
          )}

          {atmosphereImages.length > 0 && (
            <Section title="サロンの雰囲気">
              <AtmosphereCarousel salonId={salon.id} images={atmosphereImages} />
            </Section>
          )}

          {hasStoreInformation && (
            <section id="access" className="border-t border-gray-100">
              <div className="space-y-4 px-4 py-5">
                <h2 className="text-xl font-bold tracking-tight">店舗情報</h2>
                {salon.address && <InfoRow label="住所" value={salon.address} />}
                {salon.access && <InfoRow label="アクセス" value={salon.access} />}
                {salon.businessHours && <InfoRow label="営業時間" value={salon.businessHours} />}
                {salon.holiday && <InfoRow label="定休日" value={salon.holiday} />}
              </div>
              <SalonMap salon={salon} displayName={displayName} />
            </section>
          )}

        </main>
        <SalonpageFooter salon={salon} current="top" />
        {activeKodawariImage && (
          <KodawariModal
            salonId={salon.id}
            image={activeKodawariImage}
            onClose={() => setActiveKodawariImage(null)}
          />
        )}
      </div>
    </>
  );
}

function KodawariModal({ salonId, image, onClose }: { salonId: string; image: ImageItem; onClose: () => void }) {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center sm:p-6"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={`kodawari-title-${image.id}`}
        className="max-h-[90dvh] w-full max-w-[448px] overflow-y-auto rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex justify-end bg-white/95 p-3 backdrop-blur-sm">
          <button
            type="button"
            onClick={onClose}
            aria-label="閉じる"
            className="grid h-10 w-10 place-items-center rounded-full bg-gray-100 text-xl text-zinc-700"
          >
            ×
          </button>
        </div>
        <img
          src={salonpageR2ImageUrl(salonId, image.id, "gallery")}
          alt={image.alt}
          className="aspect-[4/3] w-full bg-gray-100 object-cover"
        />
        <div className="p-5 pb-8">
          <h3 id={`kodawari-title-${image.id}`} className="whitespace-pre-line text-base font-bold leading-7">
            {salonpageImageCaption(image.alt)}
          </h3>
        </div>
      </div>
    </div>
  );
}

function StaffCardMedia({ salon, member }: { salon: Salon; member: StaffItem }) {
  const imageUrl = salonpageR2ImageUrl(salon.id, member.id, "thumbnail");
  const videoUrl = member.profileVideoId ? salonpageVideoMp4Url(member.profileVideoId) : salonpageStaffVideoUrl(salon.id, member.id);

  return (
    <StaffCardVideo
      videoUrl={videoUrl}
      posterUrl={imageUrl}
      alt={member.name}
    />
  );
}

function StaffSection({ salon, staff }: { salon: Salon; staff: StaffItem[] }) {
  const visibleStaff = staff.slice(0, 3);

  return (
    <section id="staff" className="border-t border-gray-100 bg-white py-5">
      <div className="px-4">
        <div className="mb-4 flex items-end justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-xl font-bold tracking-tight">スタッフ</h2>
            <p className="mt-1 line-clamp-1 text-xs leading-5 text-zinc-500">得意なスタイルで選べる</p>
          </div>
          <span className="shrink-0 text-[11px] font-bold text-zinc-400">{staff.length}名在籍</span>
        </div>

        <div className="grid grid-cols-3 gap-2.5">
          {visibleStaff.map((member, index) => (
            <StaffProfileCard key={member.id} salon={salon} member={member} index={index} />
          ))}
        </div>

        {staff.length > 3 && (
          <a
            href={staffIndexUrl(salon)}
            className="mt-4 inline-flex h-11 w-full items-center justify-center rounded-md border border-zinc-200 bg-white px-4 text-sm font-bold text-zinc-900"
          >
            すべてのスタッフを見る
          </a>
        )}
      </div>
    </section>
  );
}

function StaffProfileCard({ salon, member, index }: { salon: Salon; member: StaffItem; index: number }) {
  const badge = staffBadge(member, index);

  return (
    <a href={staffDetailUrl(salon, member)} className="block min-w-0 overflow-hidden rounded-md bg-white">
      <StaffCardMedia salon={salon} member={member} />
      <div className="min-w-0 pt-2">
        <span className="inline-flex max-w-full rounded-sm bg-[#b43658] px-1.5 py-0.5 text-[9px] font-bold leading-3 text-white">
          <span className="truncate">{badge}</span>
        </span>
        <h3 className="mt-1.5 truncate text-sm font-bold leading-5 text-zinc-950">{member.name}</h3>
        <p className="line-clamp-1 text-[10px] font-bold leading-4 text-zinc-400">{member.history || member.role}</p>
        {member.description && <p className="mt-1 line-clamp-2 text-[10px] leading-4 text-zinc-500">{member.description}</p>}
      </div>
    </a>
  );
}

function StaffCardVideo({ videoUrl, posterUrl, alt }: { videoUrl: string; posterUrl: string; alt: string }) {
  const [hasError, setHasError] = useState(false);
  const { videoRef, isLoading } = useVideoPlayer({
    src: hasError ? null : videoUrl,
    poster: posterUrl,
    muted: true,
    loop: true,
  });

  if (hasError) {
    return <img src={posterUrl} alt={alt} className="aspect-[9/16] w-full rounded-md bg-gray-100 object-cover" />;
  }

  return (
    <div className="aspect-[9/16] overflow-hidden rounded-md bg-gray-100">
      <video
        ref={videoRef}
        aria-label={alt}
        playsInline
        muted
        loop
        preload="metadata"
        poster={posterUrl}
        onError={() => setHasError(true)}
        className="h-full w-full object-cover"
      />
    </div>
  );
}

function SalonMap({ salon, displayName }: { salon: Salon; displayName: string }) {
  const latitude = numericCoordinate(salon.latitude);
  const longitude = numericCoordinate(salon.longitude);
  const hasLocation = latitude != null && longitude != null;

  if (!hasLocation) return null;

  const position: [number, number] = [latitude, longitude];

  return (
    <div className="overflow-hidden border-t border-gray-100 bg-gray-50">
      <div className="relative">
        <MapContainer
          center={position}
          zoom={16}
          scrollWheelZoom={false}
          className="aspect-[4/3] w-full z-0"
          attributionControl={false}
          aria-label={`${displayName}の地図`}
        >
          <SalonpageTileLayer />
          <Marker position={position} icon={salonPinIcon} />
        </MapContainer>
        <GoogleMapsFloatingLink latitude={latitude} longitude={longitude} />
      </div>
    </div>
  );
}

function AtmosphereCarousel({ salonId, images }: { salonId: string; images: ImageItem[] }) {
  const hasMultipleImages = images.length > 1;
  const controlName = "atmosphere-carousel";

  return (
    <div>
      {images.map((image, index) => (
        <input
          key={image.id}
          id={atmosphereControlId(index)}
          type="radio"
          name={controlName}
          defaultChecked={index === 0}
          className="sr-only"
        />
      ))}
      <div className="atmosphere-frame relative overflow-hidden rounded-md bg-gray-100">
        {images.map((image, index) => {
          const previousIndex = (index - 1 + images.length) % images.length;
          const nextIndex = (index + 1) % images.length;

          return (
            <div key={image.id} className="atmosphere-slide relative">
              <img src={salonpageR2ImageUrl(salonId, image.id, "gallery")} alt={image.alt} className="aspect-[4/3] w-full object-cover" />
              {hasMultipleImages && (
                <>
                  <label
                    htmlFor={atmosphereControlId(previousIndex)}
                    role="button"
                    tabIndex={0}
                    aria-label="前の画像"
                    className="absolute left-2 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-xl font-bold text-zinc-800 shadow-sm"
                  >
                    ‹
                  </label>
                  <label
                    htmlFor={atmosphereControlId(nextIndex)}
                    role="button"
                    tabIndex={0}
                    aria-label="次の画像"
                    className="absolute right-2 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-xl font-bold text-zinc-800 shadow-sm"
                  >
                    ›
                  </label>
                  <div className="absolute bottom-2 right-2 rounded-full bg-black/55 px-2 py-1 text-[11px] font-bold text-white">
                    {index + 1}/{images.length}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
      {hasMultipleImages && <style>{atmosphereCarouselCss(images.length)}</style>}
    </div>
  );
}

function atmosphereControlId(index: number) {
  return `atmosphere-control-${index}`;
}

function atmosphereCarouselCss(count: number) {
  return ".atmosphere-slide { display: none; }\n" + Array.from({ length: count }, (_, index) => {
    const control = `#${atmosphereControlId(index)}`;
    const slide = `.atmosphere-slide:nth-child(${index + 1})`;

    return `
      ${control}:checked ~ .atmosphere-frame ${slide} { display: block; }
    `;
  }).join("\n");
}

function HeroMedia({ salon, displayName, imageUrl, videos, posterUrl }: { salon: Salon; displayName: string; imageUrl: string; videos: HeroVideoItem[]; posterUrl?: string | null }) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedVideo = videos[selectedIndex] || videos[0];
  const videoUrl = selectedVideo ? salonpageVideoMp4Url(selectedVideo.videoId) : null;
  const selectedPosterUrl = selectedVideo ? salonpageVideoPosterUrl(selectedVideo.videoId) : posterUrl;
  const hasVideoChoices = videos.length > 1;
  const { videoRef, isLoading } = useVideoPlayer({
    src: videoUrl ?? null,
    poster: selectedPosterUrl ?? undefined,
    muted: true,
    loop: true,
  });

  if (videoUrl) {
    return (
      <section className="relative overflow-hidden bg-zinc-950">
        <video
          key={selectedVideo?.videoId}
          ref={videoRef}
          playsInline
          muted
          loop
          poster={selectedPosterUrl ?? undefined}
          className="block h-auto w-full"
        />
        <HeroVideoSkeleton isLoading={isLoading} />
        {hasVideoChoices && (
          <div className="absolute right-3 bottom-3 left-3 flex gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {videos.slice(0, 3).map((video, index) => {
              const isSelected = index === selectedIndex;

              return (
                <button
                  key={video.videoId}
                  type="button"
                  onClick={() => setSelectedIndex(index)}
                  aria-label={`ヒーロー動画 ${index + 1}`}
                  aria-pressed={isSelected}
                  className={`h-9 w-12 overflow-hidden rounded-sm border bg-black/30 shadow-sm transition ${isSelected ? "border-white" : "border-white/45 opacity-80 hover:opacity-100"}`}
                >
                  <img
                    src={salonpageVideoThumbnailUrl(video.videoId)}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </button>
              );
            })}
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="relative bg-zinc-950">
      <div className="aspect-[4/5] overflow-hidden">
        <img src={imageUrl} alt={displayName} className="h-full w-full object-cover" />
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent p-5 text-white">
        <p className="text-4xl font-semibold tracking-tight">{displayName}</p>
        {salon.catchCopy && <p className="mt-3 max-w-[18rem] text-xs font-medium leading-5 text-white/82">{salon.catchCopy}</p>}
      </div>
    </section>
  );
}

function normalizedHeroVideos(heroVideos: HeroVideoItem[] | undefined, heroVideoId?: string | null, heroVideoOrientation?: string | null) {
  const videos = (heroVideos || []).filter((video) => video.videoId);
  if (videos.length > 0) return videos;
  return heroVideoId ? [{ videoId: heroVideoId, videoOrientation: heroVideoOrientation, position: 0 }] : [];
}

function Section({ id, title, moreHref, children }: { id?: string; title: string; moreHref?: string; children: React.ReactNode }) {
  return (
    <section id={id} className="border-t border-gray-100 p-4">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-xl font-bold tracking-tight">{title}</h2>
        {moreHref && (
          <a href={moreHref} className="text-xs font-bold text-zinc-400">
            もっと見る
          </a>
        )}
      </div>
      {children}
    </section>
  );
}

function InfoPill({ label, value, detail, title }: { label: string; value: string; detail?: string | null; title?: string | null }) {
  return (
    <dl title={title || value} className="min-w-0 rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
      <dt className="text-[11px] font-bold leading-4 text-zinc-400">{label}</dt>
      <dd className="line-clamp-2 min-w-0 text-[13px] font-bold leading-5 text-zinc-700">{value}</dd>
      {detail && <dd className="mt-0.5 line-clamp-1 min-w-0 text-[11px] leading-4 text-zinc-500">{detail}</dd>}
    </dl>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <dl className="grid gap-1 border-t border-gray-100 pt-4">
      <dt className="text-xs font-bold text-zinc-400">{label}</dt>
      <dd className="text-sm leading-6 text-zinc-700">{value}</dd>
    </dl>
  );
}

function splitList(value: string | null | undefined) {
  return (value || "").split("／").map((item) => item.trim()).filter(Boolean);
}

function formatStationName(value: string) {
  const stationName = value.trim();
  return stationName.endsWith("駅") ? stationName : `${stationName}駅`;
}

function salonDisplayName(salon: Salon) {
  return salon.name?.split("【")[0].trim() || salon.hpbSalonId;
}

function businessHoursLead(value: string) {
  const firstSentence = value.split("。")[0] || value;
  const reception = firstSentence.match(/カットは([0-9０-９]{1,2})時、その他のメニューは([0-9０-９]{1,2})時/);
  if (reception) return `カット${reception[1]}時 / 他${reception[2]}時まで`;

  const hours = firstSentence.match(/[0-9０-９]{1,2}[：:][0-9０-９]{2}[^0-9０-９]{0,3}[～〜-][^0-9０-９]{0,3}[0-9０-９]{1,2}[：:][0-9０-９]{2}/);
  return hours?.[0] || firstSentence;
}

function stylesIndexUrl(salon: Salon) {
  return `/salons/${encodeSalonpagePathId(salon.id)}/styles`;
}

function styleDetailUrl(salon: Salon, style: StyleItem) {
  return `${stylesIndexUrl(salon)}/${encodeSalonpagePathId(style.id)}`;
}

function menuDetailUrl(salon: Salon, menu: MenuItem) {
  return `/salons/${encodeSalonpagePathId(salon.id)}/menus/${encodeSalonpagePathId(menu.id)}`;
}

function menuReservationRedirectUrl(salon: Salon, menu: MenuItem) {
  const params = new URLSearchParams({ to: "reserve", salon_id: salon.id, menu_id: menu.id });
  return `/redirect?${params.toString()}`;
}

function staffDetailUrl(salon: Salon, staff: StaffItem) {
  return `/staffs/${encodeSalonpagePathId(staff.id)}`;
}

function staffIndexUrl(salon: Salon) {
  return `/salons/${encodeSalonpagePathId(salon.id)}/staffs`;
}

function staffBadge(member: StaffItem, index: number) {
  const role = member.role.replace(/[《》*]/g, "").trim();
  if (isAssistant(member)) return "アシスタント";
  if (!staffCanReserve(member)) return "相談OK";
  if (role.includes("学生人気")) return "学生人気No.1";
  if (role.includes("学生パーマ")) return "学生パーマNo.1";
  if (role.includes("No")) return "人気No.1";
  if (role.includes("パーマ")) return "パーマ特化";
  if (role.includes("スペシャリスト")) return "メンズ特化";
  return index === 0 ? "注目" : "指名OK";
}

function staffCanReserve(member: StaffItem) {
  return Boolean(member.reserveUrl) && flagEnabled(member.hasWebReservation) && !isAssistant(member);
}

function isAssistant(member: StaffItem) {
  return member.id.includes("-assistant-") || member.role.includes("アシスタント");
}

function numericCoordinate(value: number | string | null | undefined) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function flagEnabled(value: boolean | number | null | undefined) {
  return value === true || value === 1;
}

const salonPinIcon = L.divIcon({
  className: "salonpage-pin",
  html: `
    <div style="transform: translate(-50%, -100%);">
      <svg width="24" height="32" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M12 1.5C6.753 1.5 2.5 5.753 2.5 11c0 7.75 9.5 19.5 9.5 19.5S21.5 18.75 21.5 11C21.5 5.753 17.247 1.5 12 1.5Z" fill="#111827"/>
        <circle cx="12" cy="11" r="4" fill="#F9FAFB"/>
      </svg>
    </div>
  `,
  iconSize: [24, 32],
  iconAnchor: [12, 32],
});

function HeroVideoSkeleton({ isLoading }: { isLoading: boolean }) {
  return (
    <div
      aria-hidden="true"
      className={[
        "pointer-events-none absolute inset-0 transition-opacity duration-500",
        isLoading ? "opacity-100" : "opacity-0",
      ].join(" ")}
    >
      <div className="absolute inset-0 bg-neutral-900" />
      <div className="absolute inset-0 animate-shimmer bg-[linear-gradient(105deg,transparent_40%,rgba(255,255,255,0.07)_50%,transparent_60%)] bg-[length:200%_100%]" />
    </div>
  );
}
